import React from 'react';
import CollapsibleInfo from '../components/CollapsibleInfo';
import AppSelect, { APP_SELECT_FILTER_TRIGGER } from '../../shared/AppSelect';

const wizardSelectTrigger = `${APP_SELECT_FILTER_TRIGGER} px-4 py-3 rounded-lg border-gray-300`;

const currencies = [
  { code: 'INR', name: 'Indian Rupee', symbol: '₹' },
  { code: 'USD', name: 'US Dollar', symbol: '$' },
  { code: 'EUR', name: 'Euro', symbol: '€' },
  { code: 'GBP', name: 'British Pound', symbol: '£' },
  { code: 'AED', name: 'UAE Dirham', symbol: 'د.إ' },
  { code: 'SAR', name: 'Saudi Riyal', symbol: '﷼' },
  { code: 'JPY', name: 'Japanese Yen', symbol: '¥' },
  { code: 'CAD', name: 'Canadian Dollar', symbol: 'C$' },
  { code: 'AUD', name: 'Australian Dollar', symbol: 'A$' },
  { code: 'SGD', name: 'Singapore Dollar', symbol: 'S$' },
  { code: 'CNY', name: 'Chinese Yuan', symbol: '¥' },
  { code: 'HKD', name: 'Hong Kong Dollar', symbol: 'HK$' },
  { code: 'CHF', name: 'Swiss Franc', symbol: 'CHF' },
  { code: 'SEK', name: 'Swedish Krona', symbol: 'kr' },
  { code: 'NOK', name: 'Norwegian Krone', symbol: 'kr' },
  { code: 'DKK', name: 'Danish Krone', symbol: 'kr' },
  { code: 'PLN', name: 'Polish Zloty', symbol: 'zł' },
  { code: 'CZK', name: 'Czech Koruna', symbol: 'Kč' },
  { code: 'HUF', name: 'Hungarian Forint', symbol: 'Ft' },
  { code: 'BGN', name: 'Bulgarian Lev', symbol: 'лв' },
  { code: 'RON', name: 'Romanian Leu', symbol: 'lei' },
  { code: 'HRK', name: 'Croatian Kuna', symbol: 'kn' },
  { code: 'RUB', name: 'Russian Ruble', symbol: '₽' },
  { code: 'TRY', name: 'Turkish Lira', symbol: '₺' },
  { code: 'BRL', name: 'Brazilian Real', symbol: 'R$' },
  { code: 'MXN', name: 'Mexican Peso', symbol: '$' },
  { code: 'ZAR', name: 'South African Rand', symbol: 'R' },
  { code: 'KRW', name: 'South Korean Won', symbol: '₩' },
  { code: 'THB', name: 'Thai Baht', symbol: '฿' },
  { code: 'MYR', name: 'Malaysian Ringgit', symbol: 'RM' },
  { code: 'IDR', name: 'Indonesian Rupiah', symbol: 'Rp' },
  { code: 'PHP', name: 'Philippine Peso', symbol: '₱' },
  { code: 'VND', name: 'Vietnamese Dong', symbol: '₫' },
  { code: 'PKR', name: 'Pakistani Rupee', symbol: '₨' },
  { code: 'BDT', name: 'Bangladeshi Taka', symbol: '৳' },
  { code: 'LKR', name: 'Sri Lankan Rupee', symbol: '₨' },
  { code: 'NPR', name: 'Nepalese Rupee', symbol: '₨' },
  { code: 'MVR', name: 'Maldivian Rufiyaa', symbol: 'Rf' },
  { code: 'BTN', name: 'Bhutanese Ngultrum', symbol: 'Nu.' },
  { code: 'AFN', name: 'Afghan Afghani', symbol: '؋' },
  { code: 'EGP', name: 'Egyptian Pound', symbol: 'E£' },
  { code: 'QAR', name: 'Qatari Riyal', symbol: '﷼' },
  { code: 'KWD', name: 'Kuwaiti Dinar', symbol: 'د.ك' },
  { code: 'BHD', name: 'Bahraini Dinar', symbol: '.د.ب' },
  { code: 'OMR', name: 'Omani Rial', symbol: '﷼' },
  { code: 'JOD', name: 'Jordanian Dinar', symbol: 'د.ا' },
  { code: 'LBP', name: 'Lebanese Pound', symbol: 'ل.ل' },
  { code: 'ILS', name: 'Israeli Shekel', symbol: '₪' },
  { code: 'IRR', name: 'Iranian Rial', symbol: '﷼' }
];

const CompanySettingsStep = ({ data, errors, onChange, onTouch }) => {
  const handleChange = (field, value) => {
    onChange(field, value);
    onTouch(field);
  };

  const selectedCurrency = currencies.find(c => c.code === data.currency) || currencies[0];

  return (
    <div className="space-y-4">
      <div className="text-center mb-6">
        <h3 className="text-2xl font-bold text-gray-900 mb-1">Company Settings</h3>
        <p className="text-gray-600">Configure your business preferences and settings</p>
      </div>

      <div className="space-y-6">
        {/* Currency Settings */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
            <span className="text-2xl mr-3">💱</span>
            Currency Settings
          </h4>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Base Currency *
            </label>
            <AppSelect
              value={data.currency || 'INR'}
              onChange={(e) => handleChange('currency', e.target.value)}
              aria-label="Base currency"
              sheetTitle="Base currency"
              options={currencies.map((currency) => ({
                value: currency.code,
                label: `${currency.symbol} ${currency.name} (${currency.code})`,
              }))}
              triggerClassName={wizardSelectTrigger}
            />
            <p className="mt-1 text-sm text-gray-500">
              This will be used for all transactions and reports
            </p>
            
            {/* Selected Currency Display */}
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <span className="text-3xl font-bold text-blue-600 mr-3">
                    {selectedCurrency.symbol}
                  </span>
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {selectedCurrency.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {selectedCurrency.code}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-600">Selected Currency</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* GST Calculation Settings */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
            <span className="text-2xl mr-3">📊</span>
            GST Calculation
          </h4>
          
          <div className="space-y-4">
            <p className="text-sm text-gray-600 mb-3">
              Choose how GST should be calculated in your sales transactions:
            </p>
            
            <div className="grid md:grid-cols-2 gap-4">
              <label className={`cursor-pointer border-2 rounded-lg p-4 transition-colors ${
                data.gstCalculation === 'excluding' 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-gray-200 hover:border-gray-300'
              }`}>
                <input
                  type="radio"
                  name="gstCalculation"
                  value="excluding"
                  checked={data.gstCalculation === 'excluding'}
                  onChange={(e) => handleChange('gstCalculation', e.target.value)}
                  className="sr-only"
                />
                <div className="flex items-start">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mr-3 mt-0.5 ${
                    data.gstCalculation === 'excluding' 
                      ? 'border-blue-500 bg-blue-500' 
                      : 'border-gray-300'
                  }`}>
                    {data.gstCalculation === 'excluding' && (
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    )}
                  </div>
                  <div>
                    <h5 className="font-medium text-gray-900">GST Excluding</h5>
                    <p className="text-sm text-gray-600 mt-1">
                      Enter item prices without GST. GST will be calculated and added separately.
                    </p>
                    <div className="mt-2 text-sm text-blue-600">
                      Example: Item ₹100 + GST ₹18 = Total ₹118
                    </div>
                  </div>
                </div>
              </label>

              <label className={`cursor-pointer border-2 rounded-lg p-4 transition-colors ${
                data.gstCalculation === 'including' 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-gray-200 hover:border-gray-300'
              }`}>
                <input
                  type="radio"
                  name="gstCalculation"
                  value="including"
                  checked={data.gstCalculation === 'including'}
                  onChange={(e) => handleChange('gstCalculation', e.target.value)}
                  className="sr-only"
                />
                <div className="flex items-start">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mr-3 mt-0.5 ${
                    data.gstCalculation === 'including' 
                      ? 'border-blue-500 bg-blue-500' 
                      : 'border-gray-300'
                  }`}>
                    {data.gstCalculation === 'including' && (
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    )}
                  </div>
                  <div>
                    <h5 className="font-medium text-gray-900">GST Including</h5>
                    <p className="text-sm text-gray-600 mt-1">
                      Enter final selling prices with GST included. GST will be calculated backwards.
                    </p>
                    <div className="mt-2 text-sm text-blue-600">
                      Example: Total ₹118 = Item ₹100 + GST ₹18
                    </div>
                  </div>
                </div>
              </label>
            </div>
          </div>
        </div>

        {/* Business Preferences */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
            <span className="text-2xl mr-3">⚙️</span>
            Business Preferences
          </h4>
          
          <div className="space-y-4">
            {/* Financial Year */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Financial Year
              </label>
              <AppSelect
                value={data.financialYear || '2025-26'}
                onChange={(e) => handleChange('financialYear', e.target.value)}
                aria-label="Financial year"
                sheetTitle="Financial year"
                options={[
                  { value: '2024-25', label: '2024-25 (Apr 2024 - Mar 2025)' },
                  { value: '2025-26', label: '2025-26 (Apr 2025 - Mar 2026)' },
                  { value: '2026-27', label: '2026-27 (Apr 2026 - Mar 2027)' },
                ]}
                triggerClassName={wizardSelectTrigger}
              />
            </div>

            {/* Date Format */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date Format
              </label>
              <AppSelect
                value={data.dateFormat || 'DD/MM/YYYY'}
                onChange={(e) => handleChange('dateFormat', e.target.value)}
                aria-label="Date format"
                sheetTitle="Date format"
                options={[
                  { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY (31/12/2025)' },
                  { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY (12/31/2025)' },
                  { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD (2025-12-31)' },
                ]}
                triggerClassName={wizardSelectTrigger}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Collapsible Info Box */}
      <CollapsibleInfo title="Settings Information">
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• Currency setting affects all transactions and reports</li>
          <li>• GST calculation method can be changed later in settings</li>
          <li>• These preferences apply to all users in your company</li>
          <li>• You can modify these settings anytime from company settings</li>
        </ul>
      </CollapsibleInfo>
    </div>
  );
};

export default CompanySettingsStep;
